import { useCallback, useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import ProtectedRoute from "../../components/ProtectedRoute";
import { AuthProvider } from "../../context/AuthContext";
import StatsCard from "../../components/StatsCard"; // Adjust path as needed
import LineChartComponent from "../../components/charts/LineChart"; // Adjust path as needed
import PieChartComponent from "../../components/charts/PieChart"; // Adjust path as needed
import Header from "../../components/admin/Header";
import Sidebar from "../../components/admin/Sidebar"; // Adjust path as needed

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isVerified: boolean;
  lastLoginAt: string;
  profileImageUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Define interface for stat object
interface Stat {
  title: string;
  value: string;
  change: string;
  trend: "up" | "down" | "neutral";
  icon: {
    path: string;
    bgColor: string;
  };
}

// Define interface for chart data
interface LineChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    fill: boolean;
    borderColor: string;
    tension: number;
  }[];
}

interface PieChartData {
  labels: string[];
  datasets: {
    data: number[];
    backgroundColor: string[];
  }[];
}

function useTheme() {
  const [theme, setThemeState] = useState<"light" | "dark">("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const storedTheme =
      (localStorage.getItem("theme") as "light" | "dark") || "light";
    setThemeState(storedTheme);
    setMounted(true);
  }, []);

  const setTheme = useCallback((newTheme: "light" | "dark") => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);
  }, []);

  useEffect(() => {
    if (mounted) {
      document.documentElement.classList.toggle("dark", theme === "dark");
    }
  }, [theme, mounted]);

  return { theme, setTheme, mounted };
}

const statusClasses = {
  active:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  pending:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
};

const roleClasses = {
  admin:
    "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  auditor: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  staff: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
  "institutional_client":
    "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
};

const chartColors = [
  "rgba(79, 70, 229, 0.8)",
  "rgba(59, 130, 246, 0.8)",
  "rgba(16, 185, 129, 0.8)",
  "rgba(245, 158, 11, 0.8)",
  "rgba(239, 68, 68, 0.8)",
  "rgba(168, 85, 247, 0.8)",
];

// Helper functions
function formatRole(role: string): string {
  return role
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function getRoleClass(role: string): string {
  const lowerRole = role.toLowerCase().replace(/[^a-z]/g, "_");
  return roleClasses[lowerRole as keyof typeof roleClasses] || roleClasses.staff;
}

function getStatus(user: User): "active" | "pending" {
  return user.isVerified ? "active" : "pending";
}

function getStatusLabel(user: User): string {
  return user.isVerified ? "Active" : "Pending";
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

  if (diffInHours < 24) {
    return `${diffInHours} ${diffInHours === 1 ? "hour" : "hours"} ago`;
  } else {
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} ${diffInDays === 1 ? "day" : "days"} ago`;
  }
}

function AdminDashboardContent({
  onUserSelect,
}: {
  onUserSelect?: (id: string) => void;
}) {
  const { theme, setTheme } = useTheme();
  const { logout, token: authToken } = useAuth();

  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [filter, setFilter] = useState<
    "all" | "verified" | "unverified"
  >("all");
  const [stats, setStats] = useState<Stat[]>([]);
  const [lineChartData, setLineChartData] = useState<LineChartData>({
    labels: [],
    datasets: [],
  });
  const [pieChartData, setPieChartData] = useState<PieChartData>({
    labels: [],
    datasets: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!authToken) {
        throw new Error("No authentication token available");
      }
      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      const allUsers = data.data || [];

      setUsers(allUsers);

      // Calculate stats
      const totalUsers = allUsers.length;
      const verifiedUsers = allUsers.filter((user: User) => user.isVerified).length;
      const unverifiedUsers = totalUsers - verifiedUsers;

      setStats([
        {
          title: "Total Users",
          value: totalUsers.toString(),
          change: "+12% from last month",
          trend: "up" as const,
          icon: {
            path: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
            bgColor: "bg-blue-500",
          },
        },
        {
          title: "Verified Users",
          value: verifiedUsers.toString(),
          change: "+5 users",
          trend: "up" as const,
          icon: {
            path: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
            bgColor: "bg-green-500",
          },
        },
        {
          title: "Unverified Users",
          value: unverifiedUsers.toString(),
          change: "+2 users",
          trend: "neutral" as const,
          icon: {
            path: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
            bgColor: "bg-yellow-500",
          },
        },
      ]);

      // Dynamic pie chart for roles
      const roleMap: Record<string, number> = allUsers.reduce((acc, user) => {
        const roleKey = user.role;
        acc[roleKey] = (acc[roleKey] || 0) + 1;
        return acc;
      }, {});
      const roleEntries = Object.entries(roleMap).sort(([, a], [, b]) => b - a);
      const roleLabels = roleEntries.map(([role]) => formatRole(role));
      const roleData = roleEntries.map(([, count]) => count);
      const numRoles = roleLabels.length;
      const bgColors = chartColors.slice(0, numRoles);
      setPieChartData({
        labels: roleLabels,
        datasets: [
          {
            data: roleData,
            backgroundColor: bgColors,
          },
        ],
      });

      // Line chart data from createdAt
      const monthCounts: Record<string, number> = allUsers.reduce((acc, user) => {
        const date = new Date(user.createdAt);
        const yearMonth = date.toISOString().slice(0, 7); // YYYY-MM
        acc[yearMonth] = (acc[yearMonth] || 0) + 1;
        return acc;
      }, {});
      const sortedMonths = Object.keys(monthCounts).sort();
      if (sortedMonths.length > 0) {
        const labels = sortedMonths.map((m) =>
          new Date(`${m}-01`).toLocaleString("default", { month: "short" })
        );
        const data = sortedMonths.map((m) => monthCounts[m]);
        setLineChartData({
          labels,
          datasets: [
            {
              label: "User Registrations",
              data,
              fill: false,
              borderColor: "rgb(79, 70, 229)",
              tension: 0.1,
            },
          ],
        });
      } else {
        setLineChartData({
          labels: [],
          datasets: [
            {
              label: "User Registrations",
              data: [],
              fill: false,
              borderColor: "rgb(79, 70, 229)",
              tension: 0.1,
            },
          ],
        });
      }

      // Filter users based on current filter
      const filtered = allUsers.filter(
        (user: User) =>
          filter === "all" ||
          (filter === "verified" ? user.isVerified : !user.isVerified)
      );
      setFilteredUsers(filtered);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load dashboard data"
      );
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, authToken, filter]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Filter users when filter changes
  useEffect(() => {
    if (users.length > 0) {
      const filtered = users.filter(
        (user: User) =>
          filter === "all" ||
          (filter === "verified" ? user.isVerified : !user.isVerified)
      );
      setFilteredUsers(filtered);
    }
  }, [filter, users]);

  const handleAction = (action: string) => {
    switch (action) {
      case "generate-report":
        alert("Generating report...");
        break;
      case "manage-users":
        alert("Navigating to Manage Users...");
        break;
      default:
        break;
    }
  };

  const getFilterLabel = () => {
    switch (filter) {
      case "verified": return "Verified";
      case "unverified": return "Unverified";
      default: return "All";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-md border border-red-200 dark:border-red-800">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
              Error loading dashboard
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-2">
              {error}
            </p>
            <button
              onClick={fetchDashboardData}
              className="mt-3 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 px-3 py-1 rounded text-sm hover:bg-red-200 dark:hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-white">
      <Header theme={theme} setTheme={setTheme} logout={logout} />

      <div className="flex">
        <Sidebar
          onManageUsers={() => handleAction("manage-users")}
          onGenerateReport={() => handleAction("generate-report")}
        />

        <main className="flex-1 lg:ml-64">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-2xl font-semibold my-5 text-gray-900 dark:text-white">Admin Dashboard</h1>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 my-5">
              {stats.map((stat, index) => (
                <StatsCard
                  key={index}
                  title={stat.title}
                  value={stat.value}
                  change={stat.change}
                  trend={stat.trend}
                  icon={stat.icon}
                />
              ))}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
                  User Registrations Over Time
                </h3>
                <div className="h-64">
                  <LineChartComponent data={lineChartData} />
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
                  Role Distribution
                </h3>
                <div className="h-64">
                  <PieChartComponent data={pieChartData} />
                </div>
              </div>
            </div>

            {/* Filter */}
            <div className="flex justify-end my-5">
              <div className="flex bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setFilter("all")}
                  className={`px-4 py-2 text-sm font-medium rounded-l-md transition-colors ${
                    filter === "all"
                      ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilter("verified")}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    filter === "verified"
                      ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  Verified
                </button>
                <button
                  onClick={() => setFilter("unverified")}
                  className={`px-4 py-2 text-sm font-medium rounded-r-md transition-colors ${
                    filter === "unverified"
                      ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  Unverified
                </button>
              </div>
            </div>

            {/* Recent Users Grid */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Recent Users ({getFilterLabel()})
              </h2>

              {filteredUsers.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-gray-500 dark:text-gray-400">
                    No users found matching your criteria.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredUsers.slice(0, 6).map((user) => {
                    const fullName = `${user.firstName} ${user.lastName}`;
                    const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
                    const hasAvatar = user.profileImageUrl;
                    const status = getStatus(user);
                    return (
                      <div
                        key={user.id}
                        className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-shadow cursor-pointer"
                        onClick={() => onUserSelect?.(user.id)}
                      >
                        <div className="flex items-start">
                          <div className="flex-shrink-0 mr-3">
                            {hasAvatar ? (
                              <img
                                src={user.profileImageUrl!}
                                alt={`${fullName}'s avatar`}
                                className="h-10 w-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-700 dark:text-gray-300">
                                {initials}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 dark:text-white truncate">
                              {fullName}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {user.email}
                            </p>
                          </div>
                          <div className="ml-3 flex flex-col items-end space-y-1">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleClass(user.role)}`}
                            >
                              {formatRole(user.role)}
                            </span>
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusClasses[status]}`}
                            >
                              {getStatusLabel(user)}
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          {formatTimeAgo(user.lastLoginAt)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <AuthProvider>
      <ProtectedRoute allowedRoles={["ADMIN"]}>
        <AdminDashboardContent />
      </ProtectedRoute>
    </AuthProvider>
  );
}